import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Camera,
  Clock3,
  Edit3,
  FileBadge,
  FileText,
  Lock,
  Mail,
  Monitor,
  User,
} from 'lucide-react';

import styles from './Profile.module.css';
import Sidebar2 from '../../Components/SideBar2/SideBar2';
import Header2 from '../../Components/Header2/Header2';
import Avatar from '../../Assets/avat.svg';
import EditProfile from '../Profile/EditProfile';
import EditEmployment from '../Profile/EditEmployment';
import EditNextOfKinModal from '../Profile/EditNextOfKinModal';
import EditBankModal from './EditBankModal';
import EditSecurityModal from '../Profile/EditSecurityModal';
import axios from 'axios';
import { BASE_URL } from '../../Components/Api/Api';
import Swal from 'sweetalert2';
import { Spinner } from 'react-bootstrap';

function Tabs({ value, onChange, children, className }) {
  return (
    <div className={className} role="tablist" aria-label="Profile sections">
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(child, {
              isActive: child.props.value === value,
              onSelect: () => onChange(child.props.value),
            })
          : null,
      )}
    </div>
  );
}

function Tab({ label, value, isActive, onSelect }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      className={`${styles.tabButton} ${isActive ? styles.activeTab : ''}`}
      onClick={onSelect}
    >
      {label}
    </button>
  );
}

export default function Profile() {
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditPersonalOpen, setIsEditPersonalOpen] = useState(false);
  const [isEditEmploymentOpen, setIsEditEmploymentOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [isEditNextOfKinOpen, setIsEditNextOfKinOpen] = useState(false);
  const [isEditBankOpen, setIsEditBankOpen] = useState(false);
  const [isEditSecurityOpen, setIsEditSecurityOpen] = useState(false);

  const profileSections = [
    {
      key: "personal",
      label: "Personal Information",
      details: [
        ["Title", memberData?.title || "-"],
        ["First Name", memberData?.first_name || "-"],
        ["Other Name", memberData?.other_name || "-"],
        ["Last Name", memberData?.last_name || "-"],
        ["Email Address", memberData?.email || "-"],
        ["Phone Number", memberData?.phone_number || "-"],
        ["Date of Birth", memberData?.dob || "-"],
        ["Gender", memberData?.gender || "-"],
        ["Marital Status", memberData?.marital_status || "-"],
        ["Home Address", memberData?.address || "-"],
        ["Membership Number", memberData?.member_number || "-"],
        ["NIN", memberData?.nin || "-"],
      ],
      editText: "Edit Personal Information",
    },
    {
      key: "employment",
      label: "Employment Information",
      details: [
        ["Employer", memberData?.employer_name || "-"],
        ["Department", memberData?.department || "-"],
        ["Staff ID", memberData?.staff_id || "-"],
        ["Monthly Salary",
          memberData?.monthly_salary
            ? `₦${Number(memberData.monthly_salary).toLocaleString()}`
            : "-",
        ],
      ],
      editText: "Edit Employment Information",
    },
    {
      key: "contact",
      label: "Next of Kin Information",
      details: [
        ["Name", memberData?.next_of_kin_name || "-"],
        ["Relationship", memberData?.next_of_kin_relationship || "-"],
        ["Phone Number", memberData?.next_of_kin_phone || "-"],
        ["Address", memberData?.next_of_kin_address || "-"],
      ],
      editText: "Edit Next of Kin Information",
    },
    {
      key: "bank",
      label: "Bank Details",
      details: [
        ["Bank Name", memberData?.bank_name || "-"],
        ["Account Name", memberData?.bank_account_name || "-"],
        ["Account Number", memberData?.bank_account_number || "-"],
        ["BVN", memberData?.bvn || "-"],
      ],
      editText: "Edit Bank Details",
    },
    {
      key: "security",
      label: "Security",
      details: [
        ["Password", "************"],
        ["Recovery Email", memberData?.email || "-"],
      ],
      editText: "Edit Security Settings",
    },
  ];

  const activeSection = profileSections.find((section) => section.key === activeTab) || profileSections[0];

  const handleEditClick = () => {
    if (activeSection.key === 'personal') {
      setIsEditPersonalOpen(true);
    } else if (activeSection.key === 'employment') {
      setIsEditEmploymentOpen(true);
    } else if (activeSection.key === 'contact') {
      setIsEditNextOfKinOpen(true);
    } else if (activeSection.key === 'bank') {
      setIsEditBankOpen(true);
    } else if (activeSection.key === 'security') {
      setIsEditSecurityOpen(true);
    }
  };

  const documents = [
    'Passport Photograph',
    'National ID Card',
    'Proof of Address',
  ];

  const activity = [
    { icon: Clock3, label: 'Last Login', value: 'Today, 9:15 AM' },
    { icon: Monitor, label: 'Login Device', value: 'Chrome on Windows' },
    { icon: Lock, label: 'Password Updated', value: '20 May 2024' },
  ];

  const fetchMemberProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const compid = localStorage.getItem("companyId");
    const memberid = localStorage.getItem("memberId");
      const response = await axios.get(
        `${BASE_URL}/member-auth/my_profile?company_id=${compid}&member_id=${memberid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      // setMemberData(response.data.data[0]);
      setMemberData(response.data.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Unable to fetch profile.");
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (payload = {}) => {
    try {
      const token = localStorage.getItem("token");
      const companyId = memberData?.company_id || localStorage.getItem("companyId");
      const memberNumber = memberData?.member_number || localStorage.getItem("memberNumber");
      const memberid = localStorage.getItem("memberId");
      const formData = new FormData();

      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null) return;
        formData.append(key, value);
      });

      formData.append("member_number", memberNumber || "");
      formData.append("company_id", companyId || "");
      formData.append("member_id", memberid || "");

      const response = await axios.post(
        `${BASE_URL}/member-auth/update_profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
 await fetchMemberProfile();
      // Swal.fire({
      //   icon: "success",
      //   title: "Success",
      //   text: response.data.message || "Profile updated successfully.",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });

     
      return true;
    } catch (error) {
      let errorMessage = "An error occurred. Please try again.";

      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;

        if (typeof message === "string") {
          errorMessage = message;
        } else if (Array.isArray(message)) {
          errorMessage = message.join("; ");
        } else if (typeof message === "object") {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          if (Array.isArray(messages)) {
            errorMessage = messages.join("; ");
          } else {
            errorMessage = messages;
          }
        }
      }

      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: errorMessage,
      });
      return false;
    }
  };

  useEffect(() => {
    fetchMemberProfile();
  }, []);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        icon: "error",
        title: "Invalid File",
        text: "Only JPG, JPEG, PNG and WEBP images are allowed.",
      });
      e.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      Swal.fire({
        icon: "error",
        title: "File Too Large",
        text: "Image must not exceed 2MB.",
      });
      e.target.value = "";
      return;
    }

    try {
      setUploadingPhoto(true);
      const token = localStorage.getItem("token");
      const companyId = localStorage.getItem("companyId");
      const memberNumber = localStorage.getItem("memberNumber");
      const formData = new FormData();
      formData.append("passport_photograph", file);
      formData.append("member_number", memberNumber);
      formData.append("company_id", companyId);

      const response = await axios.post(
        `${BASE_URL}/member-auth/update_profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
 await fetchMemberProfile();
      // Swal.fire({
      //   icon: "success",
      //   title: "Success",
      //   text: response.data.message || "Profile photo updated successfully.",
      //   timer: 1500,
      //   showConfirmButton: false,
      // });

    
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
            
            if (error.response && error.response.data && error.response.data.message) {
              const { message } = error.response.data;
          
              if (typeof message === 'string') {
                errorMessage = message;
              } else if (Array.isArray(message)) {
                errorMessage = message.join('; ');
              } else if (typeof message === 'object') {
                // Extract the first key and its message(s)
                const firstKey = Object.keys(message)[0];
                const messages = message[firstKey];
                if (Array.isArray(messages)) {
                  errorMessage = messages.join('; ');
                } else {
                  errorMessage = messages;
                }
              }
              Swal.fire({
                icon: 'error',
                title: 'Oops...',
                text: errorMessage,
              });
          
             
            }
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  return (
    <div className={styles.container}>
      <Sidebar2 active="profile" />

      <main className={styles.mainContent}>
        <Header2 title="My Profile" />

        {/* Full-screen inline loader during initial data pull */}
       {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '1rem' }}>
            <Spinner animation="border" variant="success" />
            <p style={{ color: '#666' }}>Loading member data...</p>
          </div>
        ) : error ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
            <p>{error}</p>
            <button className="btn btn-success" onClick={fetchMemberProfile}>Retry</button>
          </div>
        ) : (
          <div className={styles.workspace}>
            <section className={styles.profileHero}>
              <div className={styles.identityBlock}>
                <div className={styles.avatarWrap}>
                  <img
                    src={memberData?.passport_photograph || Avatar}
                    alt={memberData?.first_name || "Member"}
                  />

                  <button
                    type="button"
                    className={styles.cameraButton}
                    aria-label="Change profile photo"
                    onClick={() => fileInputRef.current.click()}
                  >
                    <Camera size={18} />
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                    style={{ display: "none" }}
                    onChange={handlePhotoUpload}
                  />
                </div>

                <div className={styles.memberIntro}>
                  <div className={styles.nameLine}>
                    <h1>
                      {memberData?.first_name}
                      {/* {`${memberData?.first_name || ""} ${memberData?.last_name || ""}`.trim().length > 15
                        ? `${`${memberData?.first_name || ""} ${memberData?.last_name || ""}`.trim().slice(0, 15)}...`
                        : `${memberData?.first_name || ""} ${memberData?.last_name || ""}`.trim()} */}
                    </h1>
                    <span>Active Member</span>
                  </div>
                  <p className={styles.memberId}>{memberData?.member_number}</p>
                  <div className={styles.miniDetail}>
                    <User size={16} />Member Since {memberData?.created_at ? new Date(memberData.created_at).toLocaleDateString() : "-"}
                  </div>
                  <div className={styles.miniDetail}><Mail size={16} />{memberData?.email}</div>
                </div>
              </div>

              <div className={styles.statusPanel}>
                <div className={styles.statusGrid}>
                  <div className={styles.statusRow}>
                    <span className={styles.statusIcon}><CalendarDays size={17} /></span>
                    <span className={styles.statusLabel}>Membership Status</span>
                    <strong className={styles.activeText}>Active</strong>
                  </div>
                  <div className={styles.statusRow}>
                    <span className={styles.statusIcon}><BriefcaseBusiness size={17} /></span>
                    <span className={styles.statusLabel}>Membership Type</span>
                    <strong>{memberData?.membership_type || "Regular"}</strong>
                  </div>
                  <div className={styles.statusRow}>
                    <span className={styles.statusIcon}><FileBadge size={17} /></span>
                    <span className={styles.statusLabel}>Next AGM</span>
                    <strong>{memberData?.next_agm_date ? new Date(memberData.next_agm_date).toLocaleDateString() : "Not Set"}</strong>
                  </div>
                  <div className={styles.statusRow}>
                    <span className={styles.statusIcon}><Building2 size={17} /></span>
                    <span className={styles.statusLabel}>Branch</span>
                    <strong>{memberData?.branch || "-"}</strong>
                  </div>
                </div>
              </div>
            </section>

            <section className={styles.profileBody}>
              <Tabs value={activeTab} onChange={setActiveTab} className={styles.tabs}>
                {profileSections.map((section) => (
                  <Tab key={section.key} label={section.label} value={section.key} />
                ))}
              </Tabs>

              <div className={styles.contentGrid}>
                <div className={styles.infoCard}>
                  <h2>{activeSection.label}</h2>
                  <div className={styles.detailGrid}>
                    {activeSection.details.map(([label, value]) => (
                      <div className={styles.detailItem} key={label}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                      </div>
                    ))}
                  </div>
                  <button 
                    className={styles.editInfoBtn} 
                    type="button"
                    onClick={handleEditClick}
                  >
                    <Edit3 size={15} />{activeSection.editText}
                  </button>
                </div>

                <aside className={styles.sideStack}>
                  <div className={styles.sideCard}>
                    <div className={styles.cardTopline}>
                      <h2>Identification Documents</h2>
                      <button type="button">View All</button>
                    </div>
                    <div className={styles.documentList}>
                      {documents.map((document) => (
                        <div className={styles.documentRow} key={document}>
                          <span className={styles.documentIcon}><FileText size={17} /></span>
                          <div>
                            <strong>{document}</strong>
                            <p>Uploaded on 12 Jan 2020</p>
                          </div>
                          <span className={styles.verifiedBadge}>Verified</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={styles.sideCard}>
                    <h2>Account Activity</h2>
                    <div className={styles.activityList}>
                      {activity.map(({ icon: Icon, label, value }) => (
                        <div className={styles.activityRow} key={label}>
                          <span className={styles.activityIcon}><Icon size={16} /></span>
                          <strong>{label}</strong>
                          <span>{value}</span>
                        </div>
                      ))}
                    </div>
                    <button className={styles.changePassword} type="button" onClick={() => setIsEditSecurityOpen(true)}>
                      Change Password
                      <ArrowRight size={16} />
                    </button>
                  </div>
                </aside>
              </div>
            </section>

            {/* Modals Configuration using the unified update profile endpoint */}
            <EditProfile
              isOpen={isEditPersonalOpen}
              onClose={() => setIsEditPersonalOpen(false)}
              onSave={handleProfileUpdate}
              memberData={memberData}
            />

            <EditEmployment
              isOpen={isEditEmploymentOpen}
              onClose={() => setIsEditEmploymentOpen(false)}
              onSave={handleProfileUpdate}
              memberData={memberData}
            />

            <EditNextOfKinModal
              isOpen={isEditNextOfKinOpen}
              onClose={() => setIsEditNextOfKinOpen(false)}
              onSave={handleProfileUpdate}
              memberData={memberData}
            />

            <EditBankModal
              isOpen={isEditBankOpen}
              onClose={() => setIsEditBankOpen(false)}
              onSave={handleProfileUpdate}
              memberData={memberData}
            />

            <EditSecurityModal
              isOpen={isEditSecurityOpen}
              onClose={() => setIsEditSecurityOpen(false)}
              onSave={handleProfileUpdate}
              memberData={memberData}
            />

            {uploadingPhoto && (
              <div className={styles.loadingOverlay}>
                <Spinner animation="border" variant="success" />
                <p>Uploading profile photo...</p>
              </div>
            )}

            <footer className={styles.footer}>
              <p>&copy; 2026 RCCG Workers Fund Cooperative. All rights reserved.</p>
              <div>
                <a href="/privacy">Privacy Policy</a>
                <span />
                <a href="/terms">Terms & Conditions</a>
              </div>
            </footer>
          </div>
        )}
      </main>
    </div>
  );
}